import { Component } from "react";

/**
 * Catches render-time exceptions so one bad payload can't blank the app.
 *
 * WHY THIS EXISTS
 * The 1688/Taobao catalog renders supplier data straight from a third-party
 * API whose field shapes are not guaranteed — a variant's `options` arriving
 * as an object instead of an array, or an attribute value arriving as an
 * array where a string was expected, both throw during render. React unmounts
 * the entire tree on an uncaught render error, so a single unusual listing
 * took the whole page to white with no message. That is exactly the
 * "sometimes the details don't show" report.
 *
 * The shapes are normalized server-side now (see helpers/sourcing/ZqMapper.php), but this is
 * the backstop for the next field that changes shape without warning.
 *
 * Two intended mount points:
 *   - Around the whole app, so nothing can ever produce a blank screen.
 *   - Around a single risky subtree with a `key`, so it can fail and recover
 *     in isolation while the rest of the page keeps working.
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Kept as console.error rather than a toast: this fires during render,
    // and it's a developer signal. The user gets the fallback UI below.
    console.error("[ErrorBoundary] Uncaught render error:", error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
    if (typeof this.props.onReset === "function") {
      this.props.onReset();
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return typeof this.props.fallback === "function"
        ? this.props.fallback(this.state.error, this.handleReset)
        : this.props.fallback;
    }

    return (
      <div
        style={{
          padding: "40px 24px",
          textAlign: "center",
          color: "var(--text-main)",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}
      >
        <i
          className="fa fa-triangle-exclamation"
          style={{ fontSize: 32, color: "#f1490a", marginBottom: 16, display: "block" }}
        />
        <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700 }}>
          {this.props.title || "Something went wrong"}
        </h3>
        <p style={{ margin: "0 0 20px", color: "var(--text-muted)", fontSize: 14 }}>
          {this.props.message ||
            "We couldn't display this. Nothing was lost — you can try again."}
        </p>
        <button
          onClick={this.handleReset}
          style={{
            padding: "10px 22px",
            borderRadius: 10,
            border: "none",
            background: "#f1490a",
            color: "#fff",
            fontWeight: 600,
            fontSize: 14,
            cursor: "pointer",
          }}
        >
          {this.props.retryLabel || "Try again"}
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
